import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/api';
import DailyBarChart from '../common/DailyBarChart';

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(num);
};

const SummaryCard = ({ label, value, accent = 'text-blue-600', sublabel }) => (
  <div className="bg-white p-6 rounded-lg shadow-md">
    <h3 className="text-sm font-medium text-gray-600 mb-2">{label}</h3>
    <div className={`text-3xl font-bold ${accent}`}>{value}</div>
    {sublabel ? (
      <p className="text-xs text-gray-500 mt-1">{sublabel}</p>
    ) : null}
  </div>
);

const ByModelTable = ({ rows }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-700">Usage by Model</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
            <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Calls</th>
            <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt Tokens</th>
            <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Completion Tokens</th>
            <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Tokens</th>
            <th className="py-3 px-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Est. Cost (USD)</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.length === 0 ? (
            <tr>
              <td className="py-4 px-4 text-sm text-gray-500" colSpan={6}>
                No token usage recorded for the selected period.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.model} className="hover:bg-gray-50">
                <td className="py-3 px-4 text-sm text-gray-900 font-mono">
                  {row.model}
                  {row.is_priced ? null : (
                    <span
                      className="ml-2 inline-block rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800"
                      title="No pricing entry for this model. Cost shown as $0.00."
                    >
                      unpriced
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-gray-700 text-right">{formatNumber(row.calls)}</td>
                <td className="py-3 px-4 text-sm text-gray-700 text-right">{formatNumber(row.prompt_tokens)}</td>
                <td className="py-3 px-4 text-sm text-gray-700 text-right">{formatNumber(row.completion_tokens)}</td>
                <td className="py-3 px-4 text-sm text-gray-900 text-right font-semibold">{formatNumber(row.total_tokens)}</td>
                <td className="py-3 px-4 text-sm text-gray-900 text-right">{formatCurrency(row.total_cost)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const TokenUsageSection = ({ clinicId, clinicName }) => {
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await analyticsService.getLlmTokenUsageSummary(clinicId, days);
        setSummary(data);
      } catch (err) {
        console.error('Failed to fetch LLM token usage:', err);
        setError('Failed to load token usage data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (clinicId) {
      fetchData();
    }
  }, [clinicId, days, refreshKey]);

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading token usage data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No token usage data available for this clinic.</p>
      </div>
    );
  }

  const totals = summary.totals || {};
  const byModel = summary.by_model || [];
  const byDay = summary.by_day || [];
  const grandTotalCost = summary.grand_total_cost_usd || 0;
  const hasUnpriced = !!summary.has_unpriced_models;

  return (
    <div className="token-usage space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">LLM Token Usage</h2>
          <p className="text-sm text-gray-600">Token usage and estimated cost for {clinicName}</p>
        </div>
        <div className="flex items-center space-x-3">
          <label htmlFor="token-days-select" className="text-sm font-medium text-gray-700">Time Period:</label>
          <select
            id="token-days-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Refresh
          </button>
        </div>
      </div>

      {hasUnpriced ? (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          One or more models used in this period are not in the pricing table.
          Their cost is reported as $0.00 and they are flagged as <strong>unpriced</strong>
          in the table below. Update <code>libs/nexi_analytics/pricing.py</code> to add rates.
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard
          label="Total Tokens"
          value={formatNumber(totals.total_tokens)}
          accent="text-blue-600"
        />
        <SummaryCard
          label="Prompt Tokens"
          value={formatNumber(totals.prompt_tokens)}
          accent="text-indigo-600"
        />
        <SummaryCard
          label="Completion Tokens"
          value={formatNumber(totals.completion_tokens)}
          accent="text-purple-600"
        />
        <SummaryCard
          label="LLM Calls"
          value={formatNumber(totals.calls)}
          sublabel={`${formatNumber(totals.conversations)} conversation(s)`}
          accent="text-emerald-600"
        />
        <SummaryCard
          label="Estimated Cost"
          value={formatCurrency(grandTotalCost)}
          accent="text-rose-600"
          sublabel="USD (hardcoded rates)"
        />
      </div>

      {byDay.length > 0 && (
        <DailyBarChart
          title="Daily Total Tokens"
          data={byDay.map((row) => ({
            date: row.date,
            value: row.total_tokens,
          }))}
          formatValue={formatNumber}
        />
      )}

      <ByModelTable rows={byModel} />
    </div>
  );
};

export default TokenUsageSection;
